import chatIcon from '@/assets/icons/chat-icon.svg';
import ChatsRowItem from './ChatsRowItem';
import { Chat } from '@/types/chats';
import { useNavigate, useParams } from 'react-router-dom';
import { ROUTES } from '@/config/routes';
import { getBackUrl } from '@/utils/helpers';
import cn from 'classnames';
import { getPlainTextFromHtml } from '@/utils/messageContent';
import { useMemo } from 'react';
import UnreadCountBadge from '../../common/UnreadCountBadge';

const formatLastMessageTime = (createdAt?: string) => {
  if (!createdAt) return null;

  const date = new Date(createdAt);
  if (Number.isNaN(date.getTime())) return null;

  return date.toLocaleTimeString('ru-RU', {
    hour: '2-digit',
    minute: '2-digit',
  });
};

const ChatsListItem = ({ chat, className }: { chat: Chat; className?: string }) => {
  const { id, title, unreadCount, upload, lastMessage } = chat;
  const navigate = useNavigate();
  const isActive = useParams().chatId === id;
  const plainTextFromHtml = useMemo(
    () => getPlainTextFromHtml(lastMessage?.text ?? ''),
    [lastMessage?.text],
  );
  const lastMessageTime = formatLastMessageTime(lastMessage?.createdAt);

  const handleNavigate = () => {
    navigate(ROUTES.CHATS.BY_ID(id));
  };

  return (
    <ChatsRowItem
      id={`chat-list-item-${id}`}
      onClick={handleNavigate}
      className={cn(
        'w-full min-w-0 cursor-pointer gap-2.5 px-4 py-4 md:gap-2.5 md:border-l-2 md:py-2.5 md:pl-3.5 md:pr-4 xl:pl-[18px] xl:pr-5',
        isActive
          ? 'bg-[#fafafa] md:border-l-brand-primary md:bg-[#f8f5fa]'
          : 'md:border-l-transparent md:hover:bg-[#fafafa]',
        className,
      )}
    >
      <img
        className="h-12 w-12 shrink-0 rounded-full object-cover md:h-9 md:w-9"
        src={upload?.path ? `${getBackUrl()}/uploads/${upload.path}` : chatIcon}
        alt=""
      />
      <div className="flex w-full min-w-0 flex-col md:gap-0.5">
        <div className="flex items-center justify-between gap-2">
          <h3
            className={cn(
              'text-xl font-medium md:truncate md:text-sm',
              isActive ? 'text-inherit md:text-brand-primary' : 'text-inherit',
            )}
          >
            {title}
          </h3>
          <div className="flex shrink-0 items-center gap-1.5">
            {lastMessageTime && (
              <span className="text-sm text-lightGray md:text-xs md:tabular-nums">
                {lastMessageTime}
              </span>
            )}
            <UnreadCountBadge count={unreadCount} />
          </div>
        </div>
        <p className="line-clamp-1 text-sm text-gray md:text-xs md:leading-snug">
          {plainTextFromHtml}
        </p>
      </div>
    </ChatsRowItem>
  );
};

export default ChatsListItem;
